import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { stacks } from "@dropbox/api-v2-client";
import { Button } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import { CreateStackLine, StacksLine } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_CreateStackFromSearchResultsButton } from "@mirage/analytics/events/types/click_create_stack_from_search_results_button";
import { Chip } from "@mirage/search/General/Chip";
import { useFeatureFlagValueWithLogExposureCallback } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { useDismissableModule } from "@mirage/service-onboarding/useDismissableModule";
import { useSearchContext } from "@mirage/service-search/hooks";
import { createStack, createStackItemsBatch } from "@mirage/service-stacks";
import type { StackItemCreationFields } from "@mirage/service-stacks/types";
import Sentry from "@mirage/shared/sentry";
import { showSnackbar } from "@mirage/shared/snackbar";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { isInsideExtensionSidePanel } from "@mirage/shared/util/webExtensionsHelpers";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";

export const RESULTS_COUNT = 5;

export const CreateResultsStackChip = () => {
  const { query, results } = useSearchContext();
  // creationState tracks the state of the latest stack creation request.
  const [creationState, setCreationState] = useState<{
    pending: boolean;
    query: string;
    stack: stacks.Stack | null;
  }>({ pending: false, query: "", stack: null });
  const [hasLoggedExposure, setHasLoggedExposure] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { dismissed: tooltipDismissed, setDismissed: setTooltipDismissed } =
    useDismissableModule("dismissedCreateStackFromSearchResults");
  const [featureValue, logFeatureExposure] =
    useFeatureFlagValueWithLogExposureCallback(
      "otc_growth_2025_05_16_create_stack_from_search",
    );
  const { reportPapEvent } = useMirageAnalyticsContext();
  const navigate = useNavigate();

  const shouldRender =
    !isInsideExtensionSidePanel() &&
    convertFeatureValueToBool(featureValue) &&
    query &&
    results.length > 0;
  const hasCreatedStackForCurrentQuery =
    creationState.stack && creationState.query === query;

  // Warn if stack creation is interrupted by unload.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!creationState.pending) return;
      e.preventDefault();
      e.returnValue = true; // Included for legacy support (Chrome/Edge < 119)
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [creationState.pending]);

  // Log exposure once when rendered.
  useEffect(() => {
    if (shouldRender && !hasLoggedExposure) {
      logFeatureExposure();
      setHasLoggedExposure(true);
    }
  }, [shouldRender, hasLoggedExposure, logFeatureExposure]);

  const openStack = (stack: stacks.Stack | null) => {
    if (!stack) return;

    reportPapEvent(
      PAP_Click_CreateStackFromSearchResultsButton({
        dashActionType: "open_stack",
      }),
    );
    navigate(`${RoutePath.STACKS}/${stack.sharing_id}`);
  };

  const handleChipClick = async () => {
    // The chip is disabled in these cases, but avoid using the disabled chip state due to its appearance.
    if (results.length === 0 || creationState.pending) return;

    // Dismiss onboarding tooltip when clicking the chip.
    setTooltipDismissed(true);

    // Open the stack if one has been created for the current query.
    if (hasCreatedStackForCurrentQuery) {
      openStack(creationState.stack);
      return;
    }

    try {
      setCreationState({ pending: true, query, stack: null });
      reportPapEvent(
        PAP_Click_CreateStackFromSearchResultsButton({
          dashActionType: "create_stack",
        }),
      );
      showSnackbar({
        hideCloseButton: true,
        showSpinner: true,
        // Avoid disappearance before a success or error toast appears.
        timeoutMs: 0,
        title: i18n.t("create_stack_from_search_results_creation_toast"),
      });

      const stack = await createStack({
        name: query || "",
        description: i18n.t(
          "create_stack_from_search_results_stack_description",
          {
            query,
            date: new Date().toLocaleDateString(),
            resultsCount: RESULTS_COUNT,
          },
        ),
        isPinned: false,
        creationTypeTag: "serp_stack",
      });

      const items: StackItemCreationFields[] = results
        .slice(0, RESULTS_COUNT)
        .map((result, index) => ({
          branded_type: result.recordType[".tag"],
          id_3p: result.id3p!,
          metadata_title: result.title,
          sort_key: `a${index}`,
          url: result.url!,
        }));
      await createStackItemsBatch(stack.namespace_id!, items);

      // Only set pending and stack if query is current.
      setCreationState((prev) =>
        prev.query !== query ? prev : { ...prev, pending: false, stack },
      );
      showSnackbar({
        title: i18n.t("create_stack_from_search_results_created_toast"),
        buttons: [
          {
            label: i18n.t("create_stack_from_search_results_open_stack_label"),
            onClick: () => openStack(stack),
          },
        ],
      });
    } catch (err) {
      Sentry.captureException(err);
      showSnackbar({
        title: i18n.t("create_stack_from_search_results_error_toast"),
      });
      // Only reset pending and the stack if query is current.
      setCreationState((prev) =>
        prev.query !== query ? prev : { ...prev, pending: false, stack: null },
      );
    }
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <Chip
        label={i18n.t(
          creationState.pending
            ? "create_stack_from_search_results_creating_label"
            : hasCreatedStackForCurrentQuery
              ? "create_stack_from_search_results_open_stack_label"
              : "create_stack_from_search_results_create_label",
        )}
        onSelect={handleChipClick}
        startIcon={
          <UIIcon
            src={hasCreatedStackForCurrentQuery ? StacksLine : CreateStackLine}
          />
        }
      />
      <DigTooltip.Control
        auto
        open={!tooltipDismissed}
        placement="bottom"
        triggerRef={containerRef}
        variant="rich"
      >
        <DigTooltip.Title>
          {i18n.t("create_stack_from_search_results_tooltip_title")}
        </DigTooltip.Title>
        <DigTooltip.Text>
          {i18n.t("create_stack_from_search_results_tooltip_text", {
            count: RESULTS_COUNT,
          })}
        </DigTooltip.Text>
        <DigTooltip.Actions>
          <Button onClick={() => setTooltipDismissed(true)} variant="outline">
            {i18n.t("acknowledge_button_cta")}
          </Button>
        </DigTooltip.Actions>
      </DigTooltip.Control>
    </div>
  );
};
